/**
 * Phase 7 Plan 03 — /admin/outdated-apk PM admin tile (D-164 + NFR-A11Y-1).
 *
 * Mirrors web/src/pages/admin/QueueHealth.tsx shape verbatim — page wrapper +
 * header + table with thead/tbody. Reads /api/admin/workers/outdated-apk via
 * useOutdatedApk (TanStack Query 30s polling per D-173).
 *
 * Color rendering rules (T-7-03-02 + T-7-03-03):
 *   - color is SERVER-COMPUTED (Plan 02 outdated_apk_repository.compute_color);
 *     web does NOT call semver locally — render the precomputed verdict.
 *   - NFR-A11Y-1 no-color-only-state: every color cell ALSO carries a Lucide
 *     icon variant (CheckCircle/AlertTriangle/AlertCircle/HelpCircle), so
 *     state is never encoded in color alone.
 *
 * Default sort: worker_name ASC (server already sorts; client re-sorts
 * defensively in case the wire shape evolves).
 */
import { FormattedMessage } from "react-intl";
import {
  CheckCircle,
  AlertTriangle,
  AlertCircle,
  HelpCircle,
} from "lucide-react";
import { useOutdatedApk } from "../../hooks/useOutdatedApk";
import type { OutdatedApkRow } from "../../api/admin";
import { formatDateTime } from "../../lib/formatDateTime";

type ApkColor = OutdatedApkRow["color"];

// Chrome status colors (NOT D-69 pin-data palette). Routes through brand-token
// containers per Phase 9 Wave 2 token-mapping table:
//   green  → secondary-container (sage)
//   yellow → warning-container
//   red    → error-container
//   grey   → surface-variant
const COLOR_CELL_CLASS: Record<ApkColor, string> = {
  green: "bg-secondary-container text-on-secondary-container",
  yellow: "bg-warning-container text-warning",
  red: "bg-error-container text-error",
  grey: "bg-surface-variant text-text-secondary",
};

function StatusIcon({ color }: { color: ApkColor }) {
  if (color === "green")
    return <CheckCircle size={16} aria-hidden="true" />;
  if (color === "yellow")
    return <AlertTriangle size={16} aria-hidden="true" />;
  if (color === "red")
    return <AlertCircle size={16} aria-hidden="true" />;
  return <HelpCircle size={16} aria-hidden="true" />;
}

const COLOR_LABEL_ID: Record<ApkColor, string> = {
  green: "LABEL_OUTDATED_APK_STATUS_GREEN",
  yellow: "LABEL_OUTDATED_APK_STATUS_YELLOW",
  red: "LABEL_OUTDATED_APK_STATUS_RED",
  grey: "LABEL_OUTDATED_APK_STATUS_GREY",
};

// formatDateTime imported below — DD/MM/YYYY HH:mm per I18N-9.6.

export function OutdatedApkPage() {
  const { data: rows = [], isLoading, isError } = useOutdatedApk();
  const sorted = [...rows].sort((a, b) =>
    a.worker_name.localeCompare(b.worker_name),
  );

  return (
    <div className="ps-8 pe-8 pbs-8 pbe-8" data-testid="outdated-apk-page">
      <header className="flex items-center justify-between mbe-6">
        <h1 className="text-2xl font-semibold text-on-surface">
          <FormattedMessage id="LABEL_OUTDATED_APK_TILE_HEADING" />
        </h1>
      </header>
      {isLoading ? (
        <div data-testid="outdated-apk-loading">…</div>
      ) : isError ? (
        <div data-testid="outdated-apk-error">—</div>
      ) : (
        <table
          className="w-full bg-surface-raised border border-divider rounded-md overflow-hidden text-sm"
          data-testid="outdated-apk-table"
        >
          <thead>
            <tr className="bg-surface border-be border-divider">
              <th className="ps-4 pe-2 pbs-3 pbe-3 text-start font-semibold text-on-surface">
                <FormattedMessage id="LABEL_COL_WORKER" />
              </th>
              <th className="ps-2 pe-2 pbs-3 pbe-3 text-start font-semibold text-on-surface">
                <FormattedMessage id="LABEL_OUTDATED_APK_VERSION_COLUMN" />
              </th>
              <th className="ps-2 pe-2 pbs-3 pbe-3 text-start font-semibold text-on-surface">
                <FormattedMessage id="LABEL_OUTDATED_APK_LAST_SYNC_COLUMN" />
              </th>
              <th className="ps-2 pe-4 pbs-3 pbe-3 text-start font-semibold text-on-surface">
                <FormattedMessage id="LABEL_OUTDATED_APK_STATUS_COLUMN" />
              </th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((row) => (
              <tr
                key={row.worker_id}
                className="border-be border-divider"
                data-testid={`outdated-apk-row-${row.worker_id}`}
              >
                <td className="ps-4 pe-2 pbs-3 pbe-3 font-medium text-on-surface">
                  {row.worker_name}
                </td>
                <td className="ps-2 pe-2 pbs-3 pbe-3 text-text-secondary font-mono text-xs">
                  {row.app_version ?? "—"}
                </td>
                <td className="ps-2 pe-2 pbs-3 pbe-3 text-text-secondary">
                  {formatDateTime(row.last_sync_success_at)}
                </td>
                <td
                  data-color-cell
                  className={`ps-2 pe-4 pbs-3 pbe-3 ${COLOR_CELL_CLASS[row.color]}`}
                >
                  <span className="inline-flex items-center gap-2">
                    <StatusIcon color={row.color} />
                    <span>
                      <FormattedMessage id={COLOR_LABEL_ID[row.color]} />
                    </span>
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
